/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { v4 as uuidv4 } from 'uuid';
import React from 'react';
import PropTypes from 'prop-types';
import { Icon, TextField, EmailField, Button } from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import FeeTypeInfo from './FeeTypeInfo';
import { issuanceStatuses } from '../../utils/permitUtilsNew';
import * as types from '../../constants/index';

const propTypes = {
    index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    altGroupLeaders: PropTypes.arrayOf(PropTypes.shape({})),
    maxAlternateLeaders: PropTypes.number,
    member: PropTypes.object,
    permit: PropTypes.object.isRequired,
    issuance: PropTypes.object,
    division: PropTypes.object.isRequired,
    handleMemberInfoChange: PropTypes.func,
    removeMember: PropTypes.func,
    entryDate: PropTypes.instanceOf(gnoment),
    dateMap: PropTypes.shape({}),
    focusOnNextAdd: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

const defaultProps = {
    isDisabled: true,
};

export default class GroupMemberItem extends React.Component {
    state = {};

    constructor() {
        super();
        this.handleAlternateGroupLeaderChange =
            this.handleAlternateGroupLeaderChange.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    // eslint-disable-next-line react/sort-comp
    onClickRemove = () => {
        const index = parseInt(this.props.index, 10);
        if (index !== 0) {
            this.props.removeMember(index);
        }
    };

    handleChange(event) {
        const id = event.target.id.replace(/_\d+$/, '');
        const input = event.target.value;

        this.props.handleMemberInfoChange(this.props.index, id, input);
    }

    handleAlternateGroupLeaderChange(e) {
        if (this.alternateGroupLeaderDisabled({ onlyCheckStatus: true })) {
            e.stopPropagation();
            return null;
        }

        // if value exists, then delete it, otherwise set it to a new UUID
        const value = this.props.member.alternateGroupLeaderId ? '' : uuidv4();
        const id = e.target.id.replace(/_\d+$/, '');

        this.props.handleMemberInfoChange(this.props.index, id, value);
        return null;
    }

    // if issuance status is 0, 1 (perlim, in progress, complete)
    alternateGroupLeaderDisabled({ onlyCheckStatus }) {
        const allowedForEditing = [
            issuanceStatuses.get(types.PERMIT_PRELIMINARY),
            issuanceStatuses.get(types.PERMIT_IN_PROGRESS),
        ];
        // return early under specific conditions
        if (onlyCheckStatus) {
            return allowedForEditing.indexOf(this.props.issuance.status) < 0;
        }

        // also check groupleader if we want more than just the status (required for other inputs to be modifiable)
        return (
            this.props.member.alternateGroupLeaderId &&
            allowedForEditing.indexOf(this.props.issuance.status) < 0
        );
    }

    isDisabled() {
        return this.props.isDisabled && this.props.index === 0;
    }

    renderRemoveMemberButton() {
        if (this.isDisabled()) {
            return null;
        }

        return (
            <Button
                appearance="subtle-danger"
                onClick={this.onClickRemove}
                iconBeforeElement={<Icon iconName="delete" />}
            />
        );
    }

    renderNameRow() {
        const nameValueSet =
            this.props.member.fName !== '' && this.props.member.lName !== '';
        const permitHolderName = nameValueSet
            ? `${this.props.member.fName} ${this.props.member.lName}`
            : `#${this.props.index + 1}`;
        // Note - It should start with #2 for group members (#1 slot is for group leader) - this is purely for the label
        return (
            <div className="rec-nested-wrap">
                <div className="rec-grid-12-12">
                    <div className="lead">
                        <b>Group Member: </b>
                        {permitHolderName}
                    </div>
                    <div className="rec-nested-wrap">
                        <div className="rec-grid-4-12">
                            <div className="rec-form-item-wrap">
                                <TextField
                                    id={`memberFName_${this.props.index}`}
                                    className="memberFNameInput"
                                    label="First Name"
                                    isRequired
                                    validator={(input) =>
                                        input ? { isValid: true } : { isValid: false }
                                    }
                                    shouldValidateOnMount
                                    disabled={
                                        this.isDisabled() ||
                                        this.alternateGroupLeaderDisabled({
                                            onlyCheckStatus: false,
                                        })
                                    }
                                    value={this.props.member.fName}
                                    onChange={this.handleChange}
                                />
                            </div>
                        </div>
                        <div className="rec-grid-5-12">
                            <div className="rec-form-item-wrap">
                                <TextField
                                    id={`memberLName_${this.props.index}`}
                                    className="memberLNameInput"
                                    label="Last Name"
                                    isRequired
                                    validator={(input) =>
                                        input ? { isValid: true } : { isValid: false }
                                    }
                                    shouldValidateOnMount
                                    disabled={
                                        this.isDisabled() ||
                                        this.alternateGroupLeaderDisabled({
                                            onlyCheckStatus: false,
                                        })
                                    }
                                    value={this.props.member.lName}
                                    onChange={this.handleChange}
                                />
                            </div>
                        </div>

                        <div className="rec-grid-2-12">
                            <div className="rec-form-item-wrap">
                                <label htmlFor={`memberMName_${this.props.index}`}>
                                    MI
                                </label>
                                <input
                                    id={`memberMName_${this.props.index}`}
                                    className="memberMNameInput"
                                    type="text"
                                    readOnly={this.isDisabled()}
                                    disabled={
                                        this.isDisabled() ||
                                        this.alternateGroupLeaderDisabled({
                                            onlyCheckStatus: false,
                                        })
                                    }
                                    maxLength="1"
                                    value={this.props.member.mName}
                                    onChange={(e) => {
                                        this.handleChange(e);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="rec-grid-1-12">
                            <div className="rec-form-item-wrap">
                                {this.renderRemoveMemberButton()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderAlternatePermitHolder() {
        return (
            <div>
                {this.props.index > 0 &&
                    (this.props.member.alternateGroupLeaderId ||
                        (!this.props.member.alternateGroupLeaderId &&
                            this.props.altGroupLeaders.length <
                                this.props.maxAlternateLeaders)) && (
                        <div className="rec-nested-wrap">
                            <div className="rec-grid-12-12">
                                <fieldset>
                                    <div className="rec-grid-4-12">
                                        <legend>Leader Options</legend>
                                        <div className="rec-form-check-wrap">
                                            <label
                                                htmlFor={`memberAlternateGroupLeaderId_${this.props.index}`}
                                                className={`rec-label-checkbox ${this.alternateGroupLeaderDisabled({ onlyCheckStatus: true }) ? 'disabled' : ''}`}
                                            >
                                                <input
                                                    disabled={this.alternateGroupLeaderDisabled(
                                                        { onlyCheckStatus: true }
                                                    )}
                                                    className={`rec-input-hide ${this.alternateGroupLeaderDisabled({ onlyCheckStatus: true }) ? 'disabled' : ''}`}
                                                    type="checkbox"
                                                    onClick={
                                                        this
                                                            .handleAlternateGroupLeaderChange
                                                    }
                                                    id={`memberAlternateGroupLeaderId_${this.props.index}`}
                                                    defaultChecked={
                                                        this.props.member
                                                            .alternateGroupLeaderId !== ''
                                                    }
                                                    name="checkbox"
                                                />
                                                <span className="rec-input-checkbox" />
                                                Alternate Group Leader
                                            </label>
                                        </div>
                                    </div>
                                    {this.props.member.alternateGroupLeaderId && (
                                        <div className="rec-grid-4-12">
                                            <EmailField
                                                id="memberEmail"
                                                label="Email"
                                                isDisabled={this.alternateGroupLeaderDisabled(
                                                    { onlyCheckStatus: true }
                                                )}
                                                onChange={this.handleChange}
                                                value={this.props.member?.email ?? ''}
                                            />
                                        </div>
                                    )}
                                </fieldset>
                            </div>
                        </div>
                    )}
            </div>
        );
    }

    render() {
        return (
            <div className="rec-grid-12-12">
                <hr />
                {this.renderNameRow()}
                <FeeTypeInfo
                    index={this.props.index}
                    division={this.props.division}
                    member={this.props.member}
                    isDisabled={this.props.isDisabled}
                    handleMemberInfoChange={this.props.handleMemberInfoChange}
                />
                {this.renderAlternatePermitHolder()}
            </div>
        );
    }
}

GroupMemberItem.propTypes = propTypes;
GroupMemberItem.defaultProps = defaultProps;
